import { Form, useField, useFormikContext } from "formik";
import { Download, FileText, MapPin, Printer } from "lucide-react";
import { FormField } from "@/components/common/form-field";
import { ZipField } from "@/components/common/masked-form-field";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { AddressType } from "@/features/clients/types";
import {
  CONTACT_METHODS,
  type ContactAddressValues,
  FIELD_LABEL,
} from "@/features/clients/components/profile-form-shared";

/** Formik-bound shadcn Select for the enum contact-method fields. */
export function ContactMethodField({ name, label }: { name: string; label: string }) {
  const [field, , helpers] = useField<string>(name);
  return (
    <div className="grid gap-2">
      <Label className={FIELD_LABEL}>{label}</Label>
      <Select value={field.value || undefined} onValueChange={(v) => helpers.setValue(v)}>
        <SelectTrigger className="w-full">
          <SelectValue placeholder="Select a method" />
        </SelectTrigger>
        <SelectContent>
          {CONTACT_METHODS.map((m) => (
            <SelectItem key={m.value} value={m.value}>
              {m.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/** US/International radio, bound to a Formik AddressType field. */
function AddressTypeToggle({ name, idPrefix }: { name: string; idPrefix: string }) {
  const [field, , helpers] = useField<AddressType>(name);
  return (
    <div className="space-y-1.5">
      <Label className={FIELD_LABEL}>Location</Label>
      <RadioGroup
        value={field.value}
        onValueChange={(v) => helpers.setValue(v as AddressType)}
        className="flex items-center gap-6 pt-1.5"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem value="DOMESTIC" id={`${idPrefix}-domestic`} />
          <Label htmlFor={`${idPrefix}-domestic`} className="font-normal">
            US
          </Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem value="INTERNATIONAL" id={`${idPrefix}-international`} />
          <Label htmlFor={`${idPrefix}-international`} className="font-normal">
            Outside the US
          </Label>
        </div>
      </RadioGroup>
    </div>
  );
}

/** Address field grid for one address, shape driven by the selected type. */
function AddressFields({ type, prefix }: { type: AddressType; prefix: "home" | "mailing" }) {
  if (type === "INTERNATIONAL") {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        <div className="md:col-span-2 lg:col-span-3">
          <FormField
            name={`${prefix}InternationalAddress`}
            label="International Address"
            labelClassName={FIELD_LABEL}
          />
        </div>
        <FormField name={`${prefix}Country`} label="Country" labelClassName={FIELD_LABEL} />
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      <div className="md:col-span-2 lg:col-span-3">
        <FormField name={`${prefix}Street`} label="Street Address" labelClassName={FIELD_LABEL} />
      </div>
      <div className="md:col-span-2 lg:col-span-3">
        <FormField name={`${prefix}Line2`} label="Address Line 2" labelClassName={FIELD_LABEL} />
      </div>
      <FormField name={`${prefix}City`} label="City" labelClassName={FIELD_LABEL} />
      <FormField name={`${prefix}State`} label="State" labelClassName={FIELD_LABEL} maxLength={2} />
      <ZipField name={`${prefix}Zip`} label="ZIP Code" labelClassName={FIELD_LABEL} />
      <FormField name={`${prefix}County`} label="County" labelClassName={FIELD_LABEL} />
      <FormField name={`${prefix}Timezone`} label="Time Zone" labelClassName={FIELD_LABEL} />
    </div>
  );
}

/** "Mailing address is different" checkbox, bound to Formik. */
function MailingDifferentCheckbox() {
  const [field, , helpers] = useField<boolean>("mailingIsDifferent");
  return (
    <>
      <Checkbox
        id="mailing-different"
        checked={field.value}
        onCheckedChange={(v) => helpers.setValue(v === true)}
      />
      <Label htmlFor="mailing-different" className="text-muted-foreground font-normal">
        Mailing address is different from the address above
      </Label>
    </>
  );
}

/**
 * The full Residential & Mailing Address card, self-contained: it reads the
 * address-type / mailing-different toggles straight from Formik context, so both
 * profile forms render it with no props. `title` lets a business relabel it.
 */
export function AddressSection({ title = "Residential & Mailing Address" }: { title?: string }) {
  const { values } = useFormikContext<ContactAddressValues>();
  return (
    <Card id="address" className="scroll-mt-24 gap-0 overflow-hidden p-0">
      <div className="bg-muted/40 border-b px-6 py-4">
        <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
          <MapPin className="text-primary size-5" /> {title}
        </h3>
      </div>
      <div className="p-6">
        <div className="mb-6">
          <AddressTypeToggle name="homeAddressType" idPrefix="home" />
        </div>
        <AddressFields type={values.homeAddressType} prefix="home" />
      </div>

      <div className="flex items-center gap-2 border-t px-6 py-4">
        <MailingDifferentCheckbox />
      </div>

      {values.mailingIsDifferent && (
        <div className="border-t p-6">
          <div className="mb-6">
            <AddressTypeToggle name="mailingAddressType" idPrefix="mailing" />
          </div>
          <AddressFields type={values.mailingAddressType} prefix="mailing" />
        </div>
      )}
    </Card>
  );
}

/**
 * Free-text profile note card (legacy "explanations"), bound to Formik's
 * `explanation`. Shared by both profile forms.
 */
export function NotesSection() {
  const [field, , helpers] = useField<string>("explanation");
  return (
    <Card id="notes" className="scroll-mt-24 gap-0 overflow-hidden p-0">
      <div className="bg-muted/40 border-b px-6 py-4">
        <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
          <FileText className="text-primary size-5" /> Notes
        </h3>
      </div>
      <div className="p-6">
        <Label htmlFor="explanation" className={FIELD_LABEL}>
          Explanation / Internal Note
        </Label>
        <Textarea
          id="explanation"
          className="mt-2 min-h-28"
          value={field.value}
          onChange={(e) => helpers.setValue(e.target.value)}
          onBlur={field.onBlur}
          name={field.name}
          placeholder="Anything the team should know about this client…"
        />
      </div>
    </Card>
  );
}

/**
 * Shared page chrome for a profile form: the two-column layout (section cards +
 * sticky "jump to section" nav) and the sticky action bar. Each form supplies
 * its own section cards as `children` and its own `sections` list for the nav.
 */
export function ProfileFormShell({
  sections,
  isSubmitting,
  resetForm,
  continueRef,
  children,
  footer,
}: {
  sections: { id: string; label: string }[];
  isSubmitting: boolean;
  resetForm: () => void;
  continueRef: React.RefObject<boolean>;
  children: React.ReactNode;
  /** Independent sections (assignments, links, dependents) rendered in the
   * content column after the form cards but *inside* the form, so the sticky
   * action bar stays the last element and pins to the page bottom. */
  footer?: React.ReactNode;
}) {
  return (
    <Form>
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-12">
        <div className="flex flex-col gap-6 xl:col-span-8">
          {children}
          {footer}
        </div>

        {/* Jump to section */}
        <div className="hidden xl:col-span-4 xl:block">
          <Card className="sticky top-[144px] gap-1 p-4">
            <h4 className="text-foreground mb-2 px-2 text-sm font-bold">Jump to Section</h4>
            <nav className="space-y-1">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#${section.id}`}
                  className="text-muted-foreground hover:bg-muted block rounded-lg px-3 py-2 text-sm font-medium transition-colors"
                >
                  {section.label}
                </a>
              ))}
            </nav>
          </Card>
        </div>
      </div>

      {/* Sticky action bar */}
      <div className="bg-card sticky bottom-0 z-10 -mx-6 mt-6 -mb-6 flex flex-wrap items-center justify-between gap-3 border-t px-6 py-4">
        <div className="flex gap-3">
          <Button type="button" variant="outline" size="sm">
            <Printer /> Print
          </Button>
          <Button type="button" variant="outline" size="sm">
            <Download /> Export
          </Button>
        </div>
        <div className="flex gap-3">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => resetForm()}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="outline"
            size="sm"
            disabled={isSubmitting}
            onClick={() => {
              continueRef.current = true;
            }}
          >
            Save & Continue
          </Button>
          <Button
            type="submit"
            size="sm"
            disabled={isSubmitting}
            onClick={() => {
              // Reset the advance flag: a prior "Save & Continue" click blocked by
              // validation never reaches handleSubmit to clear it, and would
              // otherwise make this plain save navigate away.
              continueRef.current = false;
            }}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>
    </Form>
  );
}
